// src/components/Frontend/RevenueTeardown/VisualCard/VisualExplorer.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import VisualTile from "./VisualTile";
import "../revenue.css";
import GenericOverviewSideCard from "../../genericComponents/GenericOverviewSideCard";
import RevenueChatByNode from "../../Chat/RevenueChatByNode";
import { useParams } from "react-router-dom";


  const slugify = (s) => {
  if (!s) return "";
  let out = s.normalize("NFKD");                 // Unicode NFKD
  out = out.replace(/[\u0300-\u036f]/g, "");     // strip diacritics
  out = out.replace(/[^a-zA-Z0-9]+/g, "_");      // non-alnum -> _
  out = out.replace(/^_+|_+$/g, "");             // trim _
  return out.toLowerCase();
};

export default function VisualExplorer({ data, activeDim }) {
  const rows = useMemo(() => data?.root?.[activeDim] || [], [data, activeDim]);

  // AI drawer (unchanged)
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiTarget, setAiTarget] = useState("");
  const handleAIClick = (label) => { setAiTarget(label); setShowAIModal(true); };

  const dimsMemo = useMemo(() => data?.dimensions ?? [], [data]);
  const dimLabel = useCallback(
    (key) => dimsMemo.find((d) => d.key === key)?.label || key,
    [dimsMemo]
  );

  const [tiles, setTiles] = useState([]);
  const { clientName: USER_COMPANY, competitorName: COMPANY_NAME } = useParams();
  const fiscalYear = data?.context?.fiscalYear || null; // from RevenueTeardown
  const baseKey = `${activeDim}_${slugify(aiTarget)}`;
  const tableName =  baseKey;
  

  // seed first tile
  useEffect(() => {
    setTiles([{
      dimKey: activeDim,
      title: dimLabel(activeDim),
      rows,
      centerValue: data?.context?.totalRevenue,
    }]);
  }, [activeDim, rows, data, dimLabel]);

  // add a next tile to the RIGHT
  const addNextTile = (atIndex, row, dimKey) => {
    const nextRows = row.breakdowns?.[dimKey] || [];
    const nextTile = {
      dimKey,
      title: `${row.label} by ${dimLabel(dimKey)}`,
      rows: nextRows,
      centerValue: row.value,
    };
    setTiles((prev) => [...prev.slice(0, atIndex + 1), nextTile]);
  };

  // close only the LAST tile
  const closeLast = () => {
    if (tiles.length <= 1) return;
    setTiles((prev) => prev.slice(0, -1));
  };

  // clicking a collapsed rail brings that card just before the last one (expanded)
  const bringIntoFocus = (idx) => {
    if (idx >= tiles.length - 2) return; // already expanded
    setTiles((prev) => {
      const copy = [...prev];
      const picked = copy.splice(idx, 1)[0];
      copy.splice(copy.length - 1, 0, picked); // insert before last
      return copy;
    });
  };

  // rule: keep the last TWO expanded; all earlier collapsed
  const isExpandedAt = (i) => (tiles.length <= 2 ? true : i >= tiles.length - 2);

  return (
    // <div className="rt-visual-scroll no-scrollbar disable-h-scroll"> as per QA feedback removed the no scroll and disable-h-scroll classes to enable horizontal scrolling when there are many tiles
        <div className="rt-visual-scroll">

      <div className="rt-visual-row">
        {tiles.map((t, i) => {
          const expanded = isExpandedAt(i);
          const isLast = i === tiles.length - 1;

          return (
            <div
              key={`${t.title}-${i}`}
              className={`rt-visual-shell ${expanded ? "" : "collapsed"}`}
            >
              {expanded ? (
                <VisualTile
                  title={t.title}
                  centerValue={t.centerValue}
                  items={t.rows}
                  onDrill={(row, dimKey) => addNextTile(i, row, dimKey)}
                  onAIClick={(row) => handleAIClick(row.label)}
                  showClose={isLast && tiles.length > 1}
                  onClose={closeLast}
                />
              ) : (
                <CollapsedRail
                  title={t.title}
                  meta={`Total: ${t.centerValue} • ${t.length} ${t.title.toLowerCase()}`}
                  onClick={() => bringIntoFocus(i)}
                />
              )}
            </div>
          );
        })}
      </div>

      {showAIModal && (
        <GenericOverviewSideCard
          showMoreModal={showAIModal}
          setShowMoreModal={setShowAIModal}
          titleForSideCard={
            <div className="ai_insight_top_block">
              <div className="ai_insight_left">
                <div className="ai_insight_img">
                  <img src="/img/ai_insight_top.png" alt="icon" className="ai_icon" />
                </div>
                <div className="ai_insight_text">
                  <h5 className="ai_insight_top_head">Decode This: {aiTarget}</h5>
                  <p className="ai_insight_top_subhead">
                    AI-powered competitive intelligence and strategic insights
                  </p>
                </div>
              </div>
              <div className="ai_insight_right" />
            </div>
          }
          comp={
            <RevenueChatByNode
              USER_COMPANY={USER_COMPANY}
              COMPANY_NAME={COMPANY_NAME}
              table_name={tableName}
            />
          }
          minWidthForDrawer="40%"
          disableXbtn={false}
        />
      )}
    </div>
  );
}

/** Skinny collapsed rail shown for older cards */
function CollapsedRail({ title, meta, onClick }) {
  return (
    <button className="rt-rail" onClick={onClick} title={title}>
      <div className="rt-rail-stack rail-left-bottom">
        <div className="rt-rail-title">{title}</div>
        <div className="rt-rail-meta">{meta}</div>
      </div>
    </button>
  );
}
